import styled from 'styled-components';
import React from 'react';
import useAsyncEffect from 'use-async-effect';
import { AiChatMessage } from '@/features/ai-agents/shared/models';
import { ChatImageTile } from '@/features/ai-agents/components/chat-image-tile';

export interface UserMessageProps {
  message?: AiChatMessage;
}

export function UserMessage({ message }: UserMessageProps) {
  const [images, setImages] = React.useState<string[]>([]);

  // eslint-disable-next-line consistent-return
  useAsyncEffect(async () => {
    if (!message || !message.images || message.images.length === 0) {
      setImages([]);
      return;
    }
    const sourceImages = getFilteredImages(message);

    const allImages = await Promise.all(
      sourceImages
        .filter((image) => !!image?.url || !!image?.content)
        .map(imageToBase64(imageData)),
    );
    setImages(allImages);
  }, [message]);

  const imageData = (image: string): string => {
    if (image.startsWith(`http`)) {
      return image;
    }
    return `data:image/png;base64,${image}`;
  };

  return (
    <Wrapper className="ai-user-message">
      {images?.length > 0 && (
        <ImageList>
          {images?.map((image, index) => (
            <ChatImageTile canZoom imageUrl={image} key={index} />
          ))}
        </ImageList>
      )}
      <div>{message.content}</div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  font-family: Eesti, sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.6;
  padding: 1.4rem 2rem 2rem 2.6rem;
  border-radius: 2rem;

  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.09);
  border: solid 0.5px #dacdcd;
  background-color: #f8f9fb;
  max-width: 96%;
  align-self: flex-end;
  white-space: pre;
  width: fit-content;
  min-width: 10rem;

  && > div {
    width: fit-content;
    max-width: 100%;
    text-wrap: initial;
  }
`;

const ImageList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const getFilteredImages = (message: AiChatMessage) => {
  let frameSeen = false;
  return message.images.filter((image) => {
    if (image.isFrame) {
      if (frameSeen) return false;
      frameSeen = true;
      return true;
    }
    return true;
  });
};

const imageToBase64 =
  (imageData: (image: string) => string) => async (image) => {
    if (image?.url?.startsWith(`http`)) {
      return Promise.resolve(imageData(image.url));
    }
    return imageData(image?.content);
  };
